<template>
  <canvas ref="grainCanvas" class="grain-canvas" :style="{ opacity: `${opacity}` }"></canvas>
</template>

<script>
import { prefersReducedMotion } from '@/utils/motion.js';

export default {
  // Definiert die Props, die von der übergeordneten Komponente übergeben werden.
  props: {
    opacity: {
      type: Number,
      default: 0.1 // Standardwert, falls keine Opazität übergeben wird.
    }
  },
  data() {
    return {
      reduceMotion: prefersReducedMotion(),
      grainCanvas: null,
      animationFrameId: null,
      intersectionObserver: null, // Der Observer für die Sichtbarkeit
      patternCanvas: null,
      patternCtx: null,
      patternData: null,
      frame: 0,
      
      // Parameter für den Effekt
      patternSize: 240,
      patternAlpha: 24,
      patternRefreshInterval: 8,
      patternScaleX: 1,
      patternScaleY: 1,
    };
  },
  mounted() {
    this.grainCanvas = this.$refs.grainCanvas;
    if (!this.grainCanvas) return;

    // Erstellt ein verstecktes Canvas, um das Rauschmuster zu speichern.
    this.patternCanvas = document.createElement('canvas');
    this.patternCanvas.width = this.patternSize;
    this.patternCanvas.height = this.patternSize;
    this.patternCtx = this.patternCanvas.getContext('2d');
    this.patternData = this.patternCtx.createImageData(this.patternSize, this.patternSize);

    this.resizeCanvas();
    window.addEventListener('resize', this.resizeCanvas);

    // NEU: Initialisiert den Intersection Observer, um die Sichtbarkeit zu steuern
    this.initIntersectionObserver();
  },
  beforeUnmount() {
    // Entfernt die Animation und den Event-Listener beim Entladen der Komponente.
    cancelAnimationFrame(this.animationFrameId);
    window.removeEventListener('resize', this.resizeCanvas);
    
    // Stoppt die Beobachtung, wenn die Komponente entfernt wird, um Speicherlecks zu verhindern.
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  },
  methods: {
    /**
     * NEU: Initialisiert den Intersection Observer, um die Sichtbarkeit zu verfolgen.
     */
    initIntersectionObserver() {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (this.reduceMotion) {
              // Reduced-Motion: einmalig ein statisches Korn rendern, nicht animieren.
              this.updateGrain();
              this.drawGrain();
            } else {
              // Wenn das Element sichtbar ist, starte die Animation.
              this.loop();
            }
          } else {
            // Wenn das Element nicht sichtbar ist, stoppe die Animation.
            cancelAnimationFrame(this.animationFrameId);
          }
        });
      }, {
        // Die Animation startet, sobald 10% des Canvas sichtbar sind.
        threshold: 0.1 
      });

      // Beginnt mit der Beobachtung des Canvas-Elements.
      this.intersectionObserver.observe(this.grainCanvas);
    },
    /**
     * Generiert ein neues, zufälliges Rauschmuster im versteckten Canvas.
     */
    updateGrain() {
      for (let i = 0; i < this.patternSize * this.patternSize * 4; i += 4) {
        const value = (Math.random() * 255) | 0;
        this.patternData.data[i] = value;
        this.patternData.data[i + 1] = value;
        this.patternData.data[i + 2] = value;
        this.patternData.data[i + 3] = this.patternAlpha;
      }
      this.patternCtx.putImageData(this.patternData, 0, 0);
    },

    /**
     * Zeichnet das Rauschmuster auf dem sichtbaren Canvas.
     */
    drawGrain() {
      const canvas = this.grainCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = ctx.createPattern(this.patternCanvas, 'repeat');
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    },

    /**
     * Passt die Größe des Canvas an das Fenster an.
     */
    resizeCanvas() {
      const canvas = this.grainCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = window.innerWidth * this.patternScaleX;
      canvas.height = window.innerHeight * this.patternScaleY;
      ctx.scale(this.patternScaleX, this.patternScaleY);
    },

    /**
     * Die Hauptanimationsschleife.
     */
    loop() {
      if (++this.frame % this.patternRefreshInterval === 0) {
        this.updateGrain();
        this.drawGrain();
      }
      this.animationFrameId = requestAnimationFrame(this.loop);
    }
  }
};
</script>

<style scoped>
.grain-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none; /* Wichtig, damit die Maus durchklicken kann. */
}
</style>
